import React, { Fragment, useState, useEffect } from 'react';
import { GetDocumentTypes, DeleteDocument, DownloadDocument, UploadDocument, GetDocumentList } from "../../REST/utilities";
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import FileIcon from '@mui/icons-material/FileCopy';
import Button from '@mui/material/Button';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DownloadIcon from '@mui/icons-material/Download';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Access from '../../Utils/authorize';
import { dateFormat } from '../../helpers/dates'
import ToolDialogShell from '../../themes/components/ToolDialogShell';

function Documents({ docIds = [], onUpload, module }) {
    const [data, setData] = useState([])
    const [types, setTypes] = useState([])
    const [refresh, setRefresh] = useState(false)
    const [typeId, setTypeId] = useState('')

    useEffect(() => {
        (async () => {
            const resp = await GetDocumentTypes()
            const result = await GetDocumentList(docIds)
            setTypes(resp.data)
            setData(result.data)
            setTypeId((current) => current || resp.data?.[0]?._id || '')
        })()
    }, [docIds, refresh])

    const toggleRefresh = () => setRefresh(r => !r)

    const handleFile = (evt) => {
        const files = evt.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            const formData = new FormData()
            formData.append('typeId', typeId)
            formData.append('file', file)
            formData.append('createdBy', Access.getFullname())
            UploadDocument(formData).then(resp => {
                onUpload(resp.data)
                toggleRefresh()
            })
        }
        evt.target.value = '';
    }

    const handleDelete = (id) => DeleteDocument(id).then(toggleRefresh)

    const handleDownload = (id, filename) => {
        DownloadDocument(id).then(resp => {
            saveFile(resp.data, filename)
        })
    }

    const saveFile = (blob, filename) => {
        if (window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob, filename);
        } else {
            const a = document.createElement('a');
            document.body.appendChild(a);
            const url = window.URL.createObjectURL(blob);
            a.href = url;
            a.download = filename;
            a.click();
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }, 0)
        }
    }

    return <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={2}>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Upload document
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Choose a document type, then upload a file for this record.
                    </Typography>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
                    <FormControl fullWidth>
                        <TextField
                            select
                            label="Document Type"
                            value={typeId}
                            onChange={(e) => setTypeId(e.target.value)}
                            size="small"
                        >
                            {types.map((type) => (
                                <MenuItem key={type._id} value={type._id}>
                                    {type.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </FormControl>

                    <Button
                        variant="contained"
                        component="label"
                        disabled={!typeId}
                        sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
                    >
                        Upload File
                        <input
                            hidden
                            type="file"
                            accept="image/*,.pdf,.doc,.docx"
                            onChange={handleFile}
                        />
                    </Button>
                </Stack>
            </Stack>
        </Paper>

        <Stack spacing={2}>
            <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Documents
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {data.length > 0 ? `${data.length} document${data.length === 1 ? '' : 's'} attached` : 'No documents attached yet.'}
                </Typography>
            </Box>

            {data.map((value) => (
                <Paper key={value._id} variant="outlined" sx={{ p: 2 }}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, wordbreak: 'break-word' }}>
                                {value.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Added {dateFormat(value.createdAt)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Added by {value.createdBy || 'Unknown'}
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownload(value.awsId, value.name)}
                            >
                                Download
                            </Button>
                            {Access.isAdmin(module) && (
                                <IconButton color="error" onClick={() => handleDelete(value._id)} edge="end">
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Stack>
                    </Stack>
                </Paper>
            ))}
        </Stack>
    </Stack>
}


export default function DocumentView({
    docIds = [],
    onUpload,
    icon = false,
    fullWidth = false,
    module,
    open: openProp,
    onClose,
    showTrigger = true,
    buttonProps = {},
}) {
    const [internalOpen, setInternalOpen] = React.useState(false);
    const open = openProp ?? internalOpen;

    const handleClickOpen = () => {
        if (openProp === undefined) setInternalOpen(true);
    };

    const handleClose = () => {
        if (openProp === undefined) setInternalOpen(false);
        onClose?.();
    };

    return (
        <Fragment>
            {showTrigger && !icon && <Button variant="contained" startIcon={<FileCopyIcon />} onClick={handleClickOpen} {...buttonProps}>
                Documents
            </Button>}
            {showTrigger && icon && <IconButton onClick={handleClickOpen} edge="end">
                <FileIcon />
            </IconButton>}
            <ToolDialogShell open={open} onClose={handleClose} title="Documents">
                    <Documents key={new Date().toISOString()} onUpload={onUpload} module={module} docIds={docIds} />
            </ToolDialogShell>
        </Fragment>
    );
}
